import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Skeleton, Snackbar, Stack,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import { themeExpert } from '../../theme/expert';

const VIDE = { title: '', tasks: [], actif: true };

/** Une suite de tâches types, qu'on applique d'un coup à un dossier. */
function TaskListModelView() {
  const [listes, setListes] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [l, m] = await Promise.all([
        axiosInstance.get('/tasks_list_model/'),
        axiosInstance.get('/tasks_model/'),
      ]);
      setListes(l.data.results || l.data);
      setModeles(m.data.results || m.data);
      setErreur('');
    } catch {
      setErreur("Les listes n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const basculerTache = (id) => setFiche((etat) => ({
    ...etat,
    tasks: etat.tasks.includes(id)
      ? etat.tasks.filter((t) => t !== id)
      : [...etat.tasks, id],
  }));

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = { title: fiche.title.trim(), tasks: fiche.tasks,
                      actif: fiche.actif };
      if (fiche.id) {
        await axiosInstance.put(`/tasks_list_model/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/tasks_list_model/', corps);
      }
      setFiche(null);
      setMessage('Liste enregistrée');
      charger();
    } catch {
      setErreur("La liste n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/tasks_list_model/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Liste supprimée');
      charger();
    } catch {
      setErreur("La liste n'a pas pu être supprimée.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <Fiche
        titre="Listes de tâches"
        explication="Depuis un dossier, appliquer une liste y crée toutes ses tâches d'un coup."
        actions={(
          <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                  onClick={() => setFiche({ ...VIDE })}>
            Nouvelle liste
          </Button>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && listes.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucune liste pour l'instant.
          </Typography>
        )}

        {!chargement && listes.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Liste</TableCell>
                  <TableCell align="right">Tâches</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listes.map((liste) => (
                  <TableRow key={liste.id} hover
                            sx={{ opacity: liste.actif === false ? 0.5 : 1 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {liste.title}
                        </Typography>
                        {liste.actif === false && (
                          <Chip size="small" label="inactive" />
                        )}
                      </Stack>
                    </TableCell>
                    {/* Le nombre de tâches ne se voyait nulle part : il
                        fallait ouvrir la liste pour savoir ce qu'elle pose. */}
                    <TableCell align="right">
                      {liste.tasks?.length ?? 0}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small"
                                    onClick={() => setFiche({
                                      ...VIDE, ...liste,
                                      tasks: liste.tasks || [],
                                      actif: liste.actif !== false })}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(liste)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="sm">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier la liste' : 'Nouvelle liste'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField label="Nom de la liste" value={fiche?.title || ''}
                           onChange={(e) => setFiche(
                             (s) => ({ ...s, title: e.target.value }))}
                           required autoFocus
                           helperText="Ex. : ouverture d'un dégât des eaux." />

                <FormControlLabel
                  control={<Switch checked={Boolean(fiche?.actif)}
                                   onChange={(e) => setFiche(
                                     (s) => ({ ...s, actif: e.target.checked }))} />}
                  label="Proposée depuis les dossiers"
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Tâches de la liste
                    {fiche?.tasks?.length > 0 && ` (${fiche.tasks.length})`}
                  </Typography>
                  {modeles.length === 0 ? (
                    <Alert severity="info">
                      Aucun modèle de tâche. Créez-en d'abord dans l'onglet
                      « Modèles de tâches ».
                    </Alert>
                  ) : (
                    <Box sx={{ maxHeight: 280, overflowY: 'auto', p: 1,
                               border: '1px solid', borderColor: 'divider',
                               borderRadius: 1 }}>
                      {modeles.map((modele) => (
                        <FormControlLabel
                          key={modele.id}
                          sx={{ display: 'flex' }}
                          control={(
                            <Checkbox size="small"
                                      checked={Boolean(
                                        fiche?.tasks?.includes(modele.id))}
                                      onChange={() => basculerTache(modele.id)} />
                          )}
                          label={modele.title}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.title?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer cette liste ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title} » ne sera plus proposée. Les tâches qu'elle
              a déjà créées dans des dossiers ne bougent pas, et les modèles de
              tâches non plus.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default TaskListModelView;
